import { SocialLiveCasinoGameLiveStat, SocialLiveCasinoLiveStats } from './LiveStatsState'
import { LogManager } from '../../LogManager'

const COIN_TYPE = { SC: 'SC', GC: 'GC' } as const

const TABLE_STATUS = { CLOSED: 'CLOSED' } as const

const LIMITS_DIVISOR = 100

const ASSUMED_TYPE = {
    BLACKJACK: 'blackjack',
    ROULETTE: 'roulette',
    BACCARAT: 'baccarat',
    SLOT: 'slot',
} as const

const VALID_BACCARAT_WINNERS = ['banker', 'player', 'tie'] as const
type BaccaratWinner = typeof VALID_BACCARAT_WINNERS[number]

export type V3LiveTableState = {
    readonly tableId: string
    readonly provider: string
    readonly tableName: string
    readonly gameType: string
    readonly launchAlias: string
    readonly status: string
    readonly playerCount: number
    readonly seats: {
        readonly totalSeats: number
        readonly availableSeats: number
        readonly seatsAvailability: Record<string, boolean>
    } | null
    readonly limits?: ReadonlyArray<{ coinType: string; minPlay: number; maxPlay: number }>
    readonly recentResults: {
        readonly timestamp: string | null
        readonly outcome: string | null
        readonly details: Record<string, unknown> | null
    } | null
}

type GameResultExtractor = (details: Record<string, unknown>, table: V3LiveTableState) => SocialLiveCasinoGameLiveStat

function mapLimits(limits: V3LiveTableState['limits']): { XSD: number; XGC: number } {
    if (!limits) return { XSD: 0, XGC: 0 }
    const sc = limits.find(l => l.coinType === COIN_TYPE.SC)
    const gc = limits.find(l => l.coinType === COIN_TYPE.GC)
    return { XSD: sc?.minPlay ?? 0, XGC: gc?.minPlay ?? 0 }
}

function convertLimit(limit: number): number {
    return limit / LIMITS_DIVISOR
}

function baseLiveStat(table: V3LiveTableState) {
    const limits = mapLimits(table.limits)
    return {
        playerCount: table.playerCount ?? 0,
        limits: { XSD: convertLimit(limits.XSD), XGC: convertLimit(limits.XGC) },
    }
}

function mapSeatsAvailability(seats: V3LiveTableState['seats']): boolean[] | null {
    if (!seats?.seatsAvailability) return null
    return Object.keys(seats.seatsAvailability)
        .sort((a, b) => Number(b) - Number(a))
        .map(key => seats.seatsAvailability[key])
}

function isValidBaccaratWinner(value: string): value is BaccaratWinner {
    return VALID_BACCARAT_WINNERS.includes(value.toLowerCase() as BaccaratWinner)
}

const extractBlackjack: GameResultExtractor = (details, table) => ({
    ...baseLiveStat(table),
    assumedType: ASSUMED_TYPE.BLACKJACK,
    lastRoundResult: { dealerCards: Array.isArray(details.dealerCards) ? details.dealerCards : [] },
    seatsAvailability: mapSeatsAvailability(table.seats),
})

const extractRoulette: GameResultExtractor = (details, table) => {
    const previous = Array.isArray(details.previous) ? details.previous : []
    const recentNumbers = previous
        .filter(spin => spin != null)
        .map(spin => {
            const raw = typeof spin === 'object' ? (spin as { number: string }).number : spin
            return raw != null ? Number(raw) : NaN
        })
        .filter(n => !isNaN(n))

    return { ...baseLiveStat(table), assumedType: ASSUMED_TYPE.ROULETTE, latestResults: recentNumbers }
}

const OLDEST_FIRST_ALIASES = ['launch_rom_main_gbb_1']

const extractBaccarat: GameResultExtractor = (details, table) => {
    const previous = Array.isArray(details.previous) ? details.previous : []
    const outcomes = previous
        .filter((v): v is string => typeof v === 'string')
        .filter(isValidBaccaratWinner)
        .map(w => ({ score: 0, winner: w.toLowerCase() as BaccaratWinner }))

    if (OLDEST_FIRST_ALIASES.includes(table.launchAlias)) {
        outcomes.reverse()
    }

    return { ...baseLiveStat(table), assumedType: ASSUMED_TYPE.BACCARAT, latestOutcomes: outcomes }
}

const DETAILS_TYPE_REGISTRY: Record<string, GameResultExtractor> = {
    BLACKJACK: extractBlackjack,
    ROULETTE: extractRoulette,
    BACCARAT: extractBaccarat,
}

function mapTableToGameLiveStat(table: V3LiveTableState): SocialLiveCasinoGameLiveStat {
    const details = table.recentResults?.details
    const detailsType = details?.detailsType as string | undefined

    const extractor = detailsType ? DETAILS_TYPE_REGISTRY[detailsType] : undefined

    if (extractor && details) {
        return extractor(details, table)
    }

    return { ...baseLiveStat(table), assumedType: ASSUMED_TYPE.SLOT, lastRoundResult: null }
}

export function mapV3ToLiveStats(tables: V3LiveTableState[]): SocialLiveCasinoLiveStats {
    if (!Array.isArray(tables)) {
        LogManager.warn({ message: 'V3 live stats response is not an array', receivedType: typeof tables })
        return {}
    }

    const results: SocialLiveCasinoLiveStats = {}

    for (const table of tables) {
        if (!table || !table.launchAlias) continue
        if (table.status === TABLE_STATUS.CLOSED) continue
        try {
            results[table.launchAlias] = mapTableToGameLiveStat(table)
        } catch (error) {
            LogManager.warn({ message: 'Failed to map live stats table', tableId: table.tableId, launchAlias: table.launchAlias, error: String(error) })
        }
    }

    return results
}
